import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppProvider } from "@/context/AppContext";
import { Layout } from "@/components/Layout";
import { Loader2 } from "lucide-react";
import Dashboard from "@/pages/Dashboard";
import Upload from "@/pages/Upload";
import ReviewQueue from "@/pages/ReviewQueue";
import Approve from "@/pages/Approve";
import { isStandalone } from "@/hooks/useInstallPrompt";
import InvoiceDetail from "@/pages/InvoiceDetail";
import AuditLog from "@/pages/AuditLog";
import Settings from "@/pages/Settings";
import Suppliers from "@/pages/Suppliers";
import PaymentRuns from "@/pages/PaymentRuns";
import MyWeek from "@/pages/MyWeek";
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "@/pages/Auth";
import PricingPage from "@/pages/Pricing";
import BillingPage from "@/pages/Billing";
import ReportsPage from "@/pages/Reports";
import LandingPage from "@/pages/Landing";
import { LegalHubPage, LegalDocPage } from "@/pages/Legal";
import PermissionsPoliciesPage from "@/pages/PermissionsPolicies";

const Splash = () => (
  <div className="min-h-screen grid place-items-center bg-background text-muted-foreground" data-testid="auth-loading">
    <Loader2 className="h-6 w-6 animate-spin" />
  </div>
);

const Home = () => {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <LandingPage />;
  if (isStandalone() && !sessionStorage.getItem("d20-pwa-landed")) { sessionStorage.setItem("d20-pwa-landed", "1"); return <Navigate to="/approve?source=pwa" replace />; }
  return <Protected><Dashboard /></Protected>;
};

const Protected = ({ children, admin = false }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
};

const PublicOnly = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AppProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
              <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
              <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/legal" element={<LegalHubPage />} />
              <Route path="/legal/:slug" element={<LegalDocPage />} />
              <Route path="/billing" element={<Protected><BillingPage /></Protected>} />
              <Route path="/reports" element={<Protected><ReportsPage /></Protected>} />
              <Route path="/" element={<Home />} />
              <Route path="/upload" element={<Protected><Upload /></Protected>} />
              <Route path="/review" element={<Protected><ReviewQueue /></Protected>} />
              <Route path="/approve" element={<Protected><Approve /></Protected>} />
              <Route path="/invoice/:id" element={<Protected><InvoiceDetail /></Protected>} />
              <Route path="/audit" element={<Protected><AuditLog /></Protected>} />
              <Route path="/suppliers" element={<Protected><Suppliers /></Protected>} />
              <Route path="/payments" element={<Protected><PaymentRuns /></Protected>} />
              <Route path="/my-week" element={<Protected><MyWeek /></Protected>} />
              <Route path="/settings" element={<Protected admin><Settings /></Protected>} />
              <Route path="/settings/permissions" element={<Protected><PermissionsPoliciesPage /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster richColors position="top-right" />
          </BrowserRouter>
        </AppProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
